import {ElementRef, Injectable} from "@angular/core";
import {ResponsiveService} from "../orchestration/responsive.service";
import {SessionModel} from "../orchestration/session.model";
import {NativeWaiterAppService} from "../native-waiter-app.service";
import {Scanner} from "./instascan/scanner";
import {Camera} from "./instascan/camera";

declare var QRScanner: any;

// noinspection JSUnusedGlobalSymbols
export class ScannerStatus {
    authorized: boolean;
    denied: boolean;
    restricted: boolean;
    prepared: boolean;
    scanning: boolean;
    previewing: boolean;
    showing: boolean;
    lightEnabled: boolean;
    canOpenSettings: boolean;
    canEnableLight: boolean;
}

export declare interface DOMRectReadOnly {
    readonly bottom: number;
    readonly height: number;
    readonly left: number;
    readonly right: number;
    readonly top: number;
    readonly width: number;
    readonly x: number;
    readonly y: number;
}

export declare interface DetectedBarcode {
    boundingBox: DOMRectReadOnly;
    cornerPoints: {x: number, y: number}[];
    format: string;
    rawValue: string;
}

export declare class BarcodeDetector {
    constructor(barcodeDetectorOptions: {formats: string[]});
    detect(source: any): Promise<DetectedBarcode[]>;
}

@Injectable()
export class QrCodeReaderService {

    private _client: QrCodeReaderClient;

    constructor(private responsiveService: ResponsiveService,
                private sessionModel: SessionModel,
                private nativeWaiterAppService: NativeWaiterAppService) {
        console.log("--- QrCodeReaderService");
    }

    private async getClient(): Promise<QrCodeReaderClient> {
        if (!this._client) {
            // noinspection JSUnresolvedReference
            this._client = this.sessionModel.waiterMode ? new NativeWrapperQrCodeReaderClient(this.nativeWaiterAppService)
                : this.responsiveService.isApp ? new CordovaQrCodeReaderClient()
                    : "BarcodeDetector" in window && (await window["BarcodeDetector"].getSupportedFormats()).includes("qr_code") ? new BrowserQrCodeReaderClient()
                        : new InstascanQrCodeReaderClient(this.responsiveService);
            console.log("Reader client = " + this._client.constructor.name);
        }
        return this._client;
    }

    async exists(): Promise<boolean> {
        return (await this.getClient()).exists();
    }

    async prepare(elementRef: ElementRef, videoElement: ElementRef): Promise<ScannerStatus> {
        return (await this.getClient()).prepare(elementRef, videoElement);
    }

    async scan(): Promise<string> {
        return (await this.getClient()).scan();
    }

    async destroy(): Promise<void> {
        return (await this.getClient()).destroy();
    }

    async openSettings(): Promise<void> {
        return (await this.getClient()).openSettings();
    }

    async cancelScan(): Promise<void> {
        return (await this.getClient()).cancelScan();
    }

    async resumePreview(): Promise<void> {
        return (await this.getClient()).resumePreview();
    }

    async pausePreview(): Promise<void> {
        return (await this.getClient()).pausePreview();
    }

    async hide(): Promise<void> {
        return (await this.getClient()).hide();
    }

    async isNativeCamera(): Promise<boolean> {
        return (await this.getClient()).isNativeCamera();
    }
}

interface QrCodeReaderClient {

    exists(): Promise<boolean>;

    prepare(elementRef: ElementRef, videoElement: ElementRef): Promise<ScannerStatus>;

    scan(): Promise<string>;

    pausePreview(): Promise<void>;

    resumePreview(): Promise<void>;

    cancelScan(): Promise<void>;

    destroy(): Promise<void>;

    openSettings(): Promise<void>;

    hide(): Promise<void>;

    isNativeCamera(): Promise<boolean>;
}

class CordovaQrCodeReaderClient implements QrCodeReaderClient {
    elementRef: ElementRef;
    firstScan: boolean;

    async exists(): Promise<boolean> {
        return window["QRScanner"] != undefined;
    }

    // noinspection JSUnusedLocalSymbols
    async prepare(elementRef: ElementRef, videoElement: ElementRef): Promise<ScannerStatus> {
        this.elementRef = elementRef;
        this.firstScan = true;

        return new Promise<ScannerStatus>((resolve, reject) => {
            QRScanner.prepare((error: any, status: any) => {
                if (error) {
                    reject(error);
                } else {
                    if (status) {
                        resolve(status);
                    } else {
                        QRScanner.getStatus((status: any) => {
                            resolve(status);
                        });
                    }
                }
            });
        });
    }

    async scan(): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            QRScanner.scan((err: any, text: string) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(text);
                }
            });

            QRScanner.show((status) => {
                console.log("QRScanner.show: ", status);
                // This is solely to work around an issue on ios where the background does not clear the first time the camera is opened.
                // Any change to the element style appears to trigger a refresh.
                if (this.firstScan) {
                    this.elementRef.nativeElement.style.opacity = 1;
                    this.firstScan = false;
                }
            });
            if (this.firstScan) {
                this.elementRef.nativeElement.style.opacity = 0;
            }
        });
    }

    async pausePreview(): Promise<void> {
        return new Promise<void>((resolve) => {
            QRScanner.pausePreview((status) => {
                console.log("QRScanner.pausePreview: ", status);
                resolve();
            });
        });
    }

    async resumePreview(): Promise<void> {
        return new Promise<void>((resolve) => {
            QRScanner.resumePreview((status) => {
                console.log("QRScanner.resumePreview: ", status);
                resolve();
            });
        });
    }

    async cancelScan(): Promise<void> {
        return new Promise<void>((resolve) => {
            QRScanner.hide((status) => {
                console.log("QRScanner.hide: ", status);
            });
            QRScanner.cancelScan((status) => {
                console.log("QRScanner.cancelScan: ", status);
                resolve();
            });
        });
    }

    async destroy(): Promise<void> {
        return new Promise<void>((resolve) => {
            QRScanner.destroy((status) => {
                console.log("QRScanner.destroy: ", status);
                resolve();
            });
        });
    }

    async openSettings(): Promise<void> {
        return new Promise<void>((resolve) => {
            QRScanner.openSettings((status) => {
                console.log("QRScanner.openSettings: ", status);
                resolve();
            });
        });
    }

    async hide(): Promise<void> {
        return new Promise<void>((resolve) => {
            QRScanner.hide((status) => {
                console.log("QRScanner.hide: ", status);
                resolve();
            });
        });
    }

    async isNativeCamera(): Promise<boolean> {
        return true;
    }
}

class BrowserQrCodeReaderClient implements QrCodeReaderClient {

    videoElement: ElementRef;
    camera: MediaDeviceInfo;
    stream: MediaStream;
    barcodeDetector: BarcodeDetector = new BarcodeDetector({ formats: ["qr_code"] });

    async exists(): Promise<boolean> {
        return true;
    }

    async prepare(elementRef: ElementRef, videoElement: ElementRef): Promise<ScannerStatus> {
        this.videoElement = videoElement;

        const scannerStatus: ScannerStatus = new ScannerStatus();
        try {
            (await navigator.mediaDevices.getUserMedia({video: true})).getTracks().forEach((track: MediaStreamTrack) => track.stop());
        } catch (error) {
            console.log("Failed to access video ", error);
            scannerStatus.denied = true;
            return scannerStatus;
        }

        // noinspection SpellCheckingInspection
        const cameras: MediaDeviceInfo[] = (await navigator.mediaDevices.enumerateDevices()).filter((device: MediaDeviceInfo) => device.kind === "videoinput");
        if (!cameras.length) {
            scannerStatus.denied = true;
            return scannerStatus;
        }
        this.camera = cameras.find((camera: MediaDeviceInfo) => camera.label && (camera.label.indexOf("back") != -1 || camera.label.indexOf("Back") != -1));
        if (!this.camera) {
            this.camera = cameras[0];
        }
        scannerStatus.authorized = true;
        return scannerStatus;
    }

    async scan(): Promise<string> {
        return new Promise<string>((resolve) => {
            this.detect(resolve);
        });
    }

    async detect(resolve: (code: string) => void): Promise<void> {
        if (this.videoElement.nativeElement.srcObject) {
            requestAnimationFrame(() => this.detect(resolve));
            try {
                const codes: DetectedBarcode[] = await this.barcodeDetector.detect(this.videoElement.nativeElement);
                if (codes.length > 0) {
                    resolve(codes[0].rawValue);
                }
            } catch (error) {
            }
        }
    }

    async cancelScan(): Promise<void> {
        this.videoElement.nativeElement.srcObject = null;
        this.stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
    }

    async destroy(): Promise<void> {
        return this.cancelScan();
    }

    async hide(): Promise<void> {
        return this.cancelScan();
    }

    async openSettings(): Promise<void> {
    }

    async pausePreview(): Promise<void> {
        return this.cancelScan();
    }

    async resumePreview(): Promise<void> {
        const videoTrackConstraints: MediaTrackConstraints = {
            deviceId: {
                exact: this.camera.deviceId
            },
            facingMode: "environment"
        };
        this.stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: videoTrackConstraints
        });
        this.videoElement.nativeElement.srcObject = this.stream;
        this.videoElement.nativeElement.autoplay = true;
    }

    async isNativeCamera(): Promise<boolean> {
        return false;
    }
}

// noinspection SpellCheckingInspection
class InstascanQrCodeReaderClient implements QrCodeReaderClient {
    scanner: any;
    camera: any;

    constructor(private responsiveService: ResponsiveService) {
    }

    async exists(): Promise<boolean> {
        return true;
    }

    async prepare(elementRef: ElementRef, videoElement: ElementRef): Promise<ScannerStatus> {

        if (!this.scanner) {
            const scannerOpts: any = {
                video: videoElement.nativeElement,
                mirror: false,
                scanPeriod: 10
            };
            if (this.responsiveService.isIos && this.responsiveService.isSafari && parseInt(navigator.appVersion.split("Version/")[1].split(".")[0]) >= 15) {
                scannerOpts.videoWidth = 400;
                scannerOpts.videoHeight = 400;
            }
            this.scanner = new Scanner(scannerOpts);
        }

        const scannerStatus: ScannerStatus = new ScannerStatus();
        try {
            // noinspection JSUnresolvedReference
            const cameras: any[] = await Camera.getCameras();
            if (!cameras.length) {
                scannerStatus.denied = true;
                return scannerStatus;
            }
            this.camera = cameras.find((cam) => cam.name && (cam.name.indexOf("back") != -1 || cam.name.indexOf("Back") != -1));
            if (!this.camera) {
                this.camera = cameras[0];
            }
            scannerStatus.authorized = true;
            return scannerStatus;
        } catch(error) {
            console.log("Web QrCode error ", error);
            scannerStatus.denied = true;
            return scannerStatus;
        }
    }

    async scan(): Promise<string> {
        if (this.scanner) {
            return new Promise<string>((resolve) => {
                this.scanner.addListener("scan", (content) => {
                    resolve(content);
                });
                this.scanner.scan();
            });
        }
    }

    async cancelScan(): Promise<void> {
        if (this.scanner) {
            return this.scanner.stop();
        }
    }

    async destroy(): Promise<void> {
        return this.cancelScan();
    }

    async hide(): Promise<void> {
        return this.cancelScan();
    }

    async openSettings(): Promise<void> {
    }

    async pausePreview(): Promise<void> {
        return this.cancelScan();
    }

    async resumePreview(): Promise<void> {
        if (this.scanner) {
            return this.scanner.start(this.camera);
        }
    }

    async isNativeCamera(): Promise<boolean> {
        return false;
    }
}

class NativeWrapperQrCodeReaderClient implements QrCodeReaderClient {

    constructor(private nativeAppWrapperService: NativeWaiterAppService) {
    }

    async exists(): Promise<boolean> {
        return this.nativeAppWrapperService.isAvailable();
    }

    // noinspection JSUnusedLocalSymbols
    async prepare(elementRef: ElementRef, videoElement: ElementRef): Promise<ScannerStatus> {
        const scannerStatus: ScannerStatus = new ScannerStatus();
        scannerStatus.authorized = true;
        return Promise.resolve(scannerStatus);
    }

    async cancelScan(): Promise<void> {
    }

    async destroy(): Promise<void> {
    }

    async hide(): Promise<void> {
    }

    async openSettings(): Promise<void> {
        throw new Error("Not implemented");
    }

    async pausePreview(): Promise<void> {
    }

    async resumePreview(): Promise<void> {
    }

    async scan(): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const s: string = this.nativeAppWrapperService.scanBarcode();
            if (!s || s === "NOT_AUTHORISED" || "SCAN_CANCELED" === s) {
                const error: Error = new Error();
                error.name = s;
                reject(error);
            } else {
                resolve(s);
            }
        });
    }

    async isNativeCamera(): Promise<boolean> {
        return true;
    }
}



// WEBPACK FOOTER //
// ./src/app/qr-code-reader/qr-code-reader.service.ts